import React from 'react';
import { ArrowDown } from 'lucide-react';

const Hero = () => {
  const scrollToWhyItWorks = () => {
    const element = document.querySelector('#why-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-32 pb-8 sm:pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-white">
      {/* Removed background blurs -> clean white bg */}
      <div className="absolute inset-0 -z-10 bg-white"></div>

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-y-6 sm:gap-12 lg:gap-8 items-center">
          {/* Left content */}
          <div className="text-left space-y-6 sm:space-y-8 animate-fade-in-up md:max-w-xl">
            <div className="inline-block px-4 py-2 bg-[#FFF3E0] rounded-full border border-[#FFE0B2]">
              <span className="text-[#FF7F30] font-semibold text-sm tracking-wide">Language Learning for Kids 3-7</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#333333]">
              Swipe to
              <span className="block text-[#FF7F30]">Remember</span>
            </h1>

            <h2 className="text-2xl sm:text-3xl font-semibold text-[#333333] leading-tight">
              Built on Associations and Swipe Repetition
            </h2>
            
            <div className="space-y-4">
              <p className="text-lg sm:text-lg text-[#666666] leading-relaxed max-w-[calc(100%-1rem)] sm:max-w-none">
                LinguPix helps children learn new words through pictures, sound, and movement. Every card becomes a simple decision: swipe left, right, up, or down to show how well the word was remembered. That choice powers a smart repetition system, so words return at the right moment and are easier to remember for longer.
              </p>
              
              <p className="text-lg sm:text-lg font-medium text-[#333333] leading-relaxed max-w-[calc(100%-1rem)] sm:max-w-none">
                Made for parents, guardians, and language schools that want playful practice with visible progress.
              </p>
            </div>

            {/* Store Badges */}
            <div className="flex flex-col sm:flex-row gap-4 items-start pt-2">
              <a 
                href="#" 
                className="transition-transform lg:hover:scale-105 duration-200"
                aria-label="Download on the App Store"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_a0c8a310-9195-420d-8ad2-c73f20567f76/artifacts/q4g0bwgs_apple-store-btn.png" 
                  alt="Download on the App Store" 
                  className="h-[48px] sm:h-[56px] w-auto"
                />
              </a>
              <a 
                href="#" 
                className="transition-transform lg:hover:scale-105 duration-200"
                aria-label="Get it on Google Play"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_a0c8a310-9195-420d-8ad2-c73f20567f76/artifacts/8sq6qmas_play-store-btn.png" 
                  alt="Get it on Google Play" 
                  className="h-[48px] sm:h-[56px] w-auto"
                />
              </a>
            </div>

            {/* Secondary CTA */}
            <div className="pt-6 hidden sm:block">
              <button
                onClick={scrollToWhyItWorks}
                className="inline-flex items-center space-x-2 text-sm sm:text-base font-medium text-[#FF7F30]/80 hover:text-[#FF7F30] transition-colors group"
              >
                <div className="flex items-center border-b border-[#FF7F30]/30 group-hover:border-[#FF7F30] pb-0.5 transition-all duration-300">
                  <span>See how it works</span>
                  <ArrowDown className="ml-2 transition-transform duration-300 group-hover:translate-y-1" size={18} />
                </div>
              </button>
            </div>
          </div>

          {/* Right content - App mockups */}
          <div className="relative animate-fade-in-up mt-2 sm:mt-8 lg:mt-0 lg:ml-auto" style={{ animationDelay: '0.2s' }}>
            <div className="relative flex justify-center items-center h-full w-full max-w-lg mx-auto sm:space-x-4">
              
              {/* iPhone mockup - Home Page */}
              <div className="relative z-10 animate-float">
                <div className="w-48 sm:w-64 bg-white rounded-[1.75rem] sm:rounded-[2.5rem] border-[6px] sm:border-8 border-[#333333] overflow-hidden">
                  <div className="bg-[#333333] h-4 sm:h-6 rounded-b-xl sm:rounded-b-2xl mx-auto w-20 sm:w-32"></div>
                  <img
                    src="/images/home_screen.png"
                    alt="LinguPix Home Screen"
                    className="w-full h-auto"
                  />
                </div>
              </div>

              {/* iPad mockup */}
              <div className="relative animate-float -ml-16 sm:-ml-2 mt-8 sm:mt-12" style={{ animationDelay: '0.5s', zIndex: 0 }}>
                <div className="w-60 sm:w-80 bg-white rounded-[1.5rem] sm:rounded-[2rem] border-[6px] sm:border-8 border-[#333333] overflow-hidden">
                  <img
                    src="/images/learning_screen.png"
                    alt="LinguPix Learning Screenshot"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;